import { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import type { ApiError } from './apiClient'
import type { ExtendedAxiosRequestConfig } from './types'
import { getAuthTokenFromStorage, clearAuthStorage, isImpersonating } from '@/lib/utils/storage'
import { beginIntentionalSessionEnd, setLastApiRequestTime } from '@/lib/auth/sessionActivity'
import {
  GDPR_CONSENT_PENDING_KEY,
  GDPR_CONSENT_REQUIRED_EVENT,
  GdprConsentRequiredError,
} from '@/features/document-management/constants/gdprConsent'
import { ACCESS_DENIED_REDIRECT_EVENT } from '@/constants/accessDenied'

/**
 * Flag to prevent multiple simultaneous 401 redirects.
 * Once a redirect is initiated, subsequent 401 responses are ignored
 * to avoid race conditions and redundant navigation.
 */
let isRedirectingToLogin = false

/**
 * Check if an endpoint is an authentication endpoint that shouldn't trigger auto-redirect
 */
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false
  return (
    url.includes('/login') ||
    url.includes('/register') ||
    url.includes('/api/logout') ||
    url.includes('/forgotPassword') ||
    url.includes('/resetPassword') ||
    // Public invite endpoints - don't redirect on 401
    url.includes('/invite/') ||
    url.includes('/coapplicant_invite/') ||
    url.includes('/look_up') ||
    url.includes('/roles') ||
    url.includes('/api/change_role') ||
    url.includes('/pre_screening/invite')
  )
}

/**
 * Check if an endpoint should skip 401 redirect (non-critical endpoints)
 * These endpoints failing shouldn't cause the user to be logged out
 */
function shouldSkipRedirectOnError(url: string | undefined): boolean {
  if (!url) return false
  return (
    url.includes('/api/user_notification') ||
    url.includes('/api/release_notes') ||
    url.includes('/api/system_announcements') ||
    url.includes('/api/notification_count')
  )
}

/**
 * Setup request and response interceptors
 */
export function setupInterceptors(apiClient: AxiosInstance): void {
  // Request interceptor - Add auth token and handle FormData
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig & ExtendedAxiosRequestConfig) => {
      // FormData: must NOT send `application/json` (axios default) or bare `multipart/form-data`
      // (missing boundary). Strip Content-Type so the runtime sets multipart with boundary (matches Angular upload flow).
      if (config.data instanceof FormData) {
        const h = config.headers
        if (h) {
          if (typeof h.delete === 'function') {
            h.delete('Content-Type')
            h.delete('content-type')
          }
          const plain = h as unknown as Record<string, unknown>
          delete plain['Content-Type']
          delete plain['content-type']
        }
      }

      // Skip auth token if explicitly set
      if (config.skipAuth) {
        // Remove Authorization header if present
        if (config.headers) {
          delete config.headers.Authorization
          delete config.headers.token
        }
        return config
      }

      // Same as Angular HttpService.getHeaders(): `token` header (see also raw XHR using `Token`)
      const token = getAuthTokenFromStorage()

      if (token && config.headers) {
        config.headers['token'] = token
        config.headers['Token'] = token
      }

      return config
    },
    (error: AxiosError) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor - Handle errors
  apiClient.interceptors.response.use(
    (response) => {
      // Angular CustomHttpInterceptor: stamp lastapitime on successful responses
      setLastApiRequestTime(Date.now())
      return response
    },
    (error: AxiosError<ApiError | string>) => {
      // Handle different error status codes
      if (error.response) {
        const { status, data, config } = error.response
        const requestUrl = config?.url || ''
        const shouldSkipAuthRedirect = isAuthEndpoint(requestUrl)

        // Angular parity: backend uses 307 to force company/property users through
        // the GDPR service-agreement acknowledgement. Open it as a modal over the
        // dashboard (same dimmed-page pattern as Send Email). Do NOT toast —
        // Angular completes the request silently (`subscriber.complete()`).
        // Skip entirely while impersonating — Angular gates this on `!isImpersonate()`.
        if (status === 307 && !requestUrl.includes('/api/check_consent') && !isImpersonating()) {
          try {
            localStorage.removeItem('gdpr_consent_given')
            sessionStorage.setItem(GDPR_CONSENT_PENDING_KEY, '1')
          } catch {
            /* ignore */
          }

          if (window.location.pathname === '/dashboard') {
            window.dispatchEvent(new Event(GDPR_CONSENT_REQUIRED_EVENT))
          } else {
            window.location.href = '/dashboard'
          }

          return Promise.reject(new GdprConsentRequiredError())
        }

        // Handle 401 Unauthorized - Clear token and redirect to login
        // Skip redirect when:
        //   - It's an auth endpoint (login, register, etc.) — those handle their own errors
        //   - Non-critical background endpoints (notifications, release notes)
        //   - A redirect is already in progress (prevents duplicate redirects from concurrent 401s)
        const skipRedirect = shouldSkipRedirectOnError(requestUrl)

        if (status === 401 && !shouldSkipAuthRedirect && !skipRedirect && !isRedirectingToLogin) {
          // Set flag to prevent concurrent 401s from all trying to redirect
          isRedirectingToLogin = true
          beginIntentionalSessionEnd()

          clearAuthStorage()

          const redirectUrl = '/login'
          // Angular parity: surface "Your session expired" on the login page. A hard
          // redirect discards in-memory toasts, so persist it for the login page to show.
          try {
            sessionStorage.setItem('session_expired_notice', 'Your session expired. Please re-login')
          } catch {
            /* ignore */
          }
          // Use window.location for hard redirect to clear any cached state
          window.location.href = redirectUrl

          // Return early — no further error processing needed since we're navigating away
          return Promise.reject({
            message: 'Session expired. Redirecting to login...',
            status: 401,
          } as ApiError)
        }

        // Extract user-friendly error message
        let errorMessage = error.message
        let validationErrors: Record<string, string[]> | undefined = undefined

        // Handle string response (API returns plain string errors)
        if (typeof data === 'string') {
          errorMessage = data
        } else if (data && typeof data === 'object') {
          // Handle object response
          if ('message' in data && typeof data.message === 'string') {
            errorMessage = data.message
          }

          // Handle 422 validation errors - API returns errors directly on root object
          // e.g., {email: ["Please enter a valid email address."]}
          if (status === 422) {
            // Check if data has validation errors (object with array values)
            const hasValidationErrors = Object.values(data).some(v => Array.isArray(v))
            if (hasValidationErrors) {
              validationErrors = data as unknown as Record<string, string[]>
              // Create a user-friendly message from validation errors
              const errorMessages = Object.values(data)
                .filter(v => Array.isArray(v))
                .flat()
              if (errorMessages.length > 0) {
                errorMessage = errorMessages.join(', ')
              }
            }
          } else if ('errors' in data) {
            // Handle nested errors property
            validationErrors = (data as ApiError).errors
          }
        }

        // Angular CustomHttpInterceptor: Access Denied → navigate to /dashboard.
        // Component still shows Validation Message via showApiError / notify(error, 0).
        // Use a SPA event (not hard reload) so the modal on document.body stays open.
        // Optional enrichment requests can opt out (caller handles failure locally).
        const skipAccessDeniedRedirect = Boolean(
          (error.config as ExtendedAxiosRequestConfig | undefined)?.skipAccessDeniedRedirect,
        )
        if (
          errorMessage === 'Access Denied' &&
          !skipAccessDeniedRedirect &&
          typeof window !== 'undefined'
        ) {
          queueMicrotask(() => {
            window.dispatchEvent(new Event(ACCESS_DENIED_REDIRECT_EVENT))
          })
        }

        // Log errors in development mode
        if (import.meta.env.DEV) {
          console.error(`API Error [${status}]:`, {
            url: requestUrl,
            message: errorMessage,
            data,
            validationErrors,
          })
        }

        // Rejection shape: see docs/COMPANY_MODULE_MIGRATION_README.md → Cross-cutting — API errors & apiClient interceptor
        return Promise.reject({
          message: errorMessage,
          status,
          errors: validationErrors,
          // Preserve full response data for formatting (like Angular's error.error)
          data: typeof data === 'object' ? data : undefined,
        } as ApiError & { data?: Record<string, unknown> })
      }

      // Network error or other issues
      const networkError: ApiError = {
        message: error.message || 'Network error occurred. Please check your connection.',
      }

      if (import.meta.env.DEV) {
        console.error('Network Error:', error.message)
      }

      return Promise.reject(networkError)
    }
  )
}

